import {shallowMemoize} from '@sail/utils';
import createGlobalizeWithLocale from 'src/internal/createGlobalizeWithLocale/createGlobalizeWithLocale';
import resolveStyle from 'src/internal/date-formatter/resolveStyle';
import handleGlobalizeError from 'src/internal/errors/handleGlobalizeError/handleGlobalizeError';
import getLocaleData from 'src/internal/getLocaleData';
import mergeOptions from 'src/internal/mergeOptions';

import type {IAnalytics, IReports} from '@sail/observability';
import type {
  InputDate,
  StaticDateTimeStyle,
} from 'src/internal/date-formatter/types';
import type {
  FormatDateTimeOptions,
  FormatDateTimeOptionsDefaults,
  FormatDateTimeOptionsWithDefaults,
} from 'src/public/formatDateTime/types';
import type {IntlConfig, Locale} from 'src/types';

const dateFormatter = shallowMemoize(
  (
    config: IntlConfig,
    locale: Locale,
    timeZone: string,
    style: StaticDateTimeStyle,
    reports: IReports,
  ) => {
    const globalize = createGlobalizeWithLocale(
      config,
      locale,
      timeZone,
      reports,
    );

    const raw = getLocaleData(locale, config, reports).dateTimeStyles?.[style];

    try {
      return globalize.dateFormatter({
        timeZone,
        raw,
      });
    } catch (error) {
      handleGlobalizeError(error, reports);
    }
  },
);

/**
 * Format a date based on given options and returns a string with a locale-sensitive representation of the date.
 *
 * @see https://sail.stripe.me/apis/intl/useIntl/#format-date-time
 */
export default function formatDateTime(
  /** The intl config object containing the locale data. When using this API through the `useIntl` hook, this config object is automatically injected. */
  config: IntlConfig,
  reports: IReports,
  analytics: IAnalytics,
  /** The date to be formatted. */
  date: InputDate,
  /** An object with options to customize the formatting. */
  options?: FormatDateTimeOptions,
): string {
  const defaults: FormatDateTimeOptionsDefaults = {
    style: 'short-year-month-day-time',
    timeZone: config.timeZone,
    locale: config.locale,
  };

  const mergedOptions: FormatDateTimeOptionsWithDefaults = mergeOptions(
    config,
    defaults,
    reports,
    options,
  );

  // Etc/Unknown is a non-existing timezone, but we have a non-trivial amount
  // of users hitting us with it. So we re-map it to UTC, which is the best we
  // can do.
  if (mergedOptions.timeZone === 'Etc/Unknown') {
    mergedOptions.timeZone = 'Etc/UTC';
  }

  const style = resolveStyle(date, mergedOptions, config, reports, analytics);

  const formatter = dateFormatter(
    config,
    mergedOptions.locale,
    mergedOptions.timeZone,
    style,
    reports,
  );

  return formatter(typeof date === 'number' ? new Date(date) : date);
}

export type FormatDateTime = typeof formatDateTime;

export type FormatDateTimeDeclarative = (
  date: Parameters<FormatDateTime>[3],
  options?: Parameters<FormatDateTime>[4],
) => ReturnType<FormatDateTime>;
